import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookSummaryDetail() {
  const { slug } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [slug]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/book-summaries/${slug}`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching book summary:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-text-secondary text-xl" data-testid="summary-loading">Loading...</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-text-secondary text-xl mb-4">Book summary not found</p>
          <Link to="/book-summaries">
            <Button className="btn-secondary">Back to Summaries</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      {summary && (
        <Helmet>
          <title>{summary.book_title} Summary - Star Nivesh</title>
          <meta name="description" content={summary.meta_description || `Summary of ${summary.book_title} by ${summary.author} - Key insights and takeaways from this investment book.`} />
          <meta name="robots" content={summary.meta_robots || "index, follow"} />
          <meta property="og:title" content={`${summary.book_title} by ${summary.author}`} />
          <meta property="og:description" content={summary.meta_description || `Summary of ${summary.book_title}`} />
          <meta property="og:image" content={summary.image_url} />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${summary.book_title} by ${summary.author}`} />
          <meta name="twitter:description" content={summary.meta_description || `Summary of ${summary.book_title}`} />
          <meta name="twitter:image" content={summary.image_url} />
        </Helmet>
      )}
      <article className="py-12" style={{backgroundColor: "#2C3540"}} data-testid="summary-detail">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/book-summaries">
            <Button variant="ghost" className="mb-8" data-testid="summary-back-button">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Book Summaries
            </Button>
          </Link>

          <div className="glass-effect rounded-2xl p-8 md:p-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-text-primary mb-4" data-testid="summary-title">
              {summary.book_title}
            </h1>
            <p className="text-xl text-text-muted mb-8">by {summary.author}</p>

            <div className="blog-content prose prose-invert prose-lg max-w-none mb-12" data-testid="summary-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary.summary}</ReactMarkdown>
            </div>

            <div className="border-t border-white/10 pt-8">
              <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">Key Takeaways</h2>
              <ul className="space-y-4">
                {summary.key_takeaways.map((takeaway, index) => (
                  <li key={index} className="flex items-start" data-testid={`takeaway-${index}`}>
                    <CheckCircle2 className="h-6 w-6 text-secondary mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary text-lg">{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center glass-effect p-8 rounded-2xl">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">
              Want to discuss these concepts for your portfolio?
            </h3>
            <Link to="/contact">
              <Button className="btn-primary" data-testid="summary-cta-button">Get in Touch</Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}